import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Spinner, Placeholder } from "react-bootstrap";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  ArrowRight,
  Clock,
  Flame,
  Folder,
  Hourglass,
  Medal,
  ShieldAlert,
  Trophy,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import AppShell from "../../components/AppShell";
import NotificationIcon from "../../components/Notification";
import PageHeader from "../../components/PageHeader";
import StudentCalendar from "../../components/StudentCalendar";
import { useUser } from "../../contextProviders/userContext";
import "./StudentDashboard.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const BLUE = "#1B3A87";
const LINE = "#D8DFEC";
const INK = "#17213A";
const FONT = "Source Sans 3";
const numberFormat = new Intl.NumberFormat("en-ZA", { maximumFractionDigits: 1 });

const ordinal = (rank) => {
  const value = Number(rank) || 0;
  const remainder100 = value % 100;
  if (remainder100 >= 11 && remainder100 <= 13) return `${value}th`;
  if (value % 10 === 1) return `${value}st`;
  if (value % 10 === 2) return `${value}nd`;
  if (value % 10 === 3) return `${value}rd`;
  return `${value}th`;
};

const Metric = ({ label, value, detail, icon: Icon }) => (
  <div className="student-metric">
    <div className="student-metric__copy">
      <span>{label}</span>
      <strong className="wcco-technical">{value}</strong>
      <small>{detail}</small>
    </div>
    <Icon size={20} aria-hidden="true" />
  </div>
);

const SectionHeader = ({ title, description, action }) => (
  <div className="student-section-header">
    <div>
      <h2>{title}</h2>
      {description && <p>{description}</p>}
    </div>
    {action}
  </div>
);

const RankMark = ({ rank }) => (
  <span className={`student-rank-mark${rank <= 3 ? ` student-rank-mark--top-${rank}` : ""}`}>
    {rank === 1 && <Medal size={13} aria-hidden="true" />}
    {ordinal(rank)}
  </span>
);

const RankingBoard = ({ data, loading, type, expanded, onToggle, currentUser }) => {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!expanded || !search.trim()) return data || [];
    const query = search.trim().toLowerCase();
    return (data || []).filter((item) => (item.name || "").toLowerCase().includes(query));
  }, [expanded, data, search]);

  if (loading) {
    return <div className="student-panel-state"><Spinner animation="border" size="sm" /><span>Loading rankings…</span></div>;
  }
  if (!data?.length) return <div className="student-panel-state">No approved hours have been recorded yet.</div>;

  const visible = expanded ? filtered : data.slice(0, 10);
  const showPersonalPosition = type === "volunteers" && currentUser;

  return (
    <div className={`student-ranking-list${expanded ? " is-expanded" : ""}`}>
      {showPersonalPosition && (
        <div className="student-personal-rank">
          <div>
            <span>Your position</span>
            <strong>{currentUser.rank ? ordinal(currentUser.rank) : "—"}</strong>
          </div>
          <div>
            <span>Approved hours</span>
            <strong className="wcco-technical">{numberFormat.format(currentUser.hours || 0)} hrs</strong>
          </div>
        </div>
      )}

      {expanded && data.length > 10 && (
        <div className="student-ranking-list__search">
          <input
            type="search"
            placeholder={`Filter ${type === "volunteers" ? "volunteers" : "projects"} by name…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      <div className="student-ranking-list__labels" aria-hidden="true">
        <span>Rank</span><span>{type === "volunteers" ? "Volunteer" : "Project"}</span><span>Hours</span>
      </div>
      <ol className="student-ranking-list__rows">
        {visible.map((item, index) => {
          const rank = Number(item.rank) || index + 1;
          return (
            <li key={`${item.name}-${rank}`} className={item.isCurrentUser ? "is-current-user" : ""}>
              <RankMark rank={rank} />
              <div className="student-ranking-list__identity">
                <strong title={item.name}>{item.name}</strong>
                {item.isCurrentUser && <span>You</span>}
              </div>
              <span className="student-ranking-list__hours wcco-technical">{numberFormat.format(item.hours || 0)} hrs</span>
            </li>
          );
        })}
        {expanded && filtered.length === 0 && (
          <li className="student-panel-state" style={{ padding: "var(--space-4) 0", justifyContent: "center" }}>
            No {type === "volunteers" ? "volunteers" : "projects"} match "{search}"
          </li>
        )}
      </ol>

      {data.length > 10 && (
        <div className="student-ranking-list__footer">
          <Button
            type="button"
            variant="link"
            className="student-ranking-list__toggle"
            onClick={() => {
              onToggle();
              if (expanded) setSearch("");
            }}
          >
            {expanded ? "Show top 10" : `View all ${data.length}`}
          </Button>
          {expanded && (
            <span className="student-ranking-list__count">
              Showing {filtered.length} of {data.length}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [projects, setProjects] = useState([]);
  const [participationData, setParticipationData] = useState({});
  const [leaderboard, setLeaderboard] = useState({ topVolunteers: [], topProjects: [] });
  const [loading, setLoading] = useState(true);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedBoards, setExpandedBoards] = useState({ volunteers: false, projects: false });

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.get("/participations/my-projects");
      const projectRows = response.data?.success
        ? response.data.data || []
        : (response.data || []).filter((item) => item?.project).map((item) => item.project);
      
      setProjects(projectRows);

      // Extract participation logs directly from the optimized backend response
      const participationMap = {};
      projectRows.forEach((project) => {
        participationMap[project._id] = { logs: project.logs || [] };
      });
      setParticipationData(participationMap);
    } catch (requestError) {
      console.error("Error fetching student dashboard:", requestError);
      setError("Your dashboard data could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    try {
      setLeaderboardLoading(true);
      const response = await api.get("/participations/leaderboard");
      setLeaderboard(response.data || { topVolunteers: [], topProjects: [] });
    } catch (requestError) {
      console.error("Error fetching leaderboard:", requestError);
    } finally {
      setLeaderboardLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchLeaderboard();
  }, [fetchDashboard, fetchLeaderboard]);

  const dashboard = useMemo(() => {
    const logs = Object.values(participationData).flatMap((participation) => participation?.logs || []);
    const approvedHours = logs
      .filter((log) => log.status === "approved")
      .reduce((sum, log) => sum + (Number(log.hours) || 0), 0);
    const pendingHours = logs
      .filter((log) => log.status === "pending")
      .reduce((sum, log) => sum + (Number(log.hours) || 0), 0);
    const projectHours = projects.map((project) => {
      const projectLogs = participationData[project._id]?.logs || [];
      return {
        ...project,
        approvedHours: projectLogs.filter((log) => log.status === "approved").reduce((sum, log) => sum + (Number(log.hours) || 0), 0),
        pendingHours: projectLogs.filter((log) => log.status === "pending").reduce((sum, log) => sum + (Number(log.hours) || 0), 0),
        allHours: projectLogs.reduce((sum, log) => sum + (Number(log.hours) || 0), 0),
      };
    });
    const categories = new Map();
    projects.forEach((project) => (project.categories || []).forEach((category) => categories.set(category, (categories.get(category) || 0) + 1)));
    const categoryRows = [...categories.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
    const currentUser = leaderboard.topVolunteers?.find((item) => item.isCurrentUser) || null;

    return { logs, approvedHours, pendingHours, projectHours, categoryRows, currentUser };
  }, [leaderboard.topVolunteers, participationData, projects]);

  const chartData = {
    labels: dashboard.projectHours.map((project) => project.name),
    datasets: [
      { label: "Approved", data: dashboard.projectHours.map((project) => project.approvedHours), backgroundColor: BLUE, borderRadius: 2, barThickness: 13 },
      { label: "Pending", data: dashboard.projectHours.map((project) => project.pendingHours), backgroundColor: "rgba(27, 58, 135, 0.30)", borderRadius: 2, barThickness: 13 },
    ],
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    animation: { duration: 250 },
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, color: INK, font: { family: FONT, size: 12 } } },
      tooltip: { displayColors: false, titleFont: { family: FONT }, bodyFont: { family: FONT } },
    },
    scales: {
      x: { stacked: true, beginAtZero: true, grid: { color: LINE }, border: { display: false }, ticks: { color: INK, precision: 0, font: { family: FONT, size: 11 } } },
      y: { stacked: true, grid: { display: false }, border: { color: LINE }, ticks: { color: INK, font: { family: FONT, size: 11 }, callback(value) { const label = this.getLabelForValue(value); return label.length > 24 ? `${label.slice(0, 23)}…` : label; } } },
    },
  };

  const hourTotal = dashboard.approvedHours + dashboard.pendingHours;
  const approvalShare = hourTotal ? (dashboard.approvedHours / hourTotal) * 100 : 0;
  const greeting = new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 18 ? "Good afternoon" : "Good evening";

  if (loading) {
    return (
      <AppShell>
        <main className="student-dashboard">
          <PageHeader
            title={<Placeholder animation="glow"><Placeholder xs={4} /></Placeholder>}
            description={<Placeholder animation="glow"><Placeholder xs={8} /></Placeholder>}
            actions={
              <Placeholder animation="glow">
                <Placeholder.Button variant="primary" xs={12} style={{ width: '120px' }} />
              </Placeholder>
            }
          />
          <section className="student-metrics" aria-hidden="true">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="student-metric">
                <div className="student-metric__copy w-100">
                  <span><Placeholder animation="glow"><Placeholder xs={6} /></Placeholder></span>
                  <strong className="wcco-technical"><Placeholder animation="glow"><Placeholder xs={4} size="lg" /></Placeholder></strong>
                  <small><Placeholder animation="glow"><Placeholder xs={8} /></Placeholder></small>
                </div>
                <Placeholder animation="glow" className="ms-2">
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#e9ecef', borderRadius: '50%' }}></div>
                </Placeholder>
              </div>
            ))}
          </section>

          <section className="student-dashboard-grid student-dashboard-grid--overview" aria-hidden="true">
            <article className="student-dashboard-panel student-dashboard-panel--chart">
              <SectionHeader title={<Placeholder animation="glow"><Placeholder xs={4} /></Placeholder>} />
              <Placeholder animation="glow">
                <div style={{ height: '300px', backgroundColor: '#e9ecef', borderRadius: '4px' }}></div>
              </Placeholder>
            </article>

            <article className="student-dashboard-panel">
              <SectionHeader title={<Placeholder animation="glow"><Placeholder xs={6} /></Placeholder>} />
              <Placeholder animation="glow">
                 <div style={{ height: '40px', backgroundColor: '#e9ecef', borderRadius: '4px', marginBottom: '16px' }}></div>
                 <div style={{ height: '20px', backgroundColor: '#e9ecef', borderRadius: '4px', marginBottom: '12px' }}></div>
                 <div style={{ height: '20px', backgroundColor: '#e9ecef', borderRadius: '4px', marginBottom: '12px' }}></div>
                 <div style={{ height: '20px', backgroundColor: '#e9ecef', borderRadius: '4px', marginBottom: '12px' }}></div>
              </Placeholder>
            </article>
          </section>
        </main>
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell>
        <Alert className="d-flex align-items-center gap-2"><ShieldAlert size={18} />{error}<Button className="ms-auto" onClick={fetchDashboard}>Try again</Button></Alert>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <main className="student-dashboard">
        <PageHeader
          title={`${greeting}, ${user?.name || "Student"}`}
          description="Track your approved contribution, pending activity, projects, and position in the volunteer community."
          actions={<><NotificationIcon /><Button onClick={() => navigate("/myprojects")}>My projects <ArrowRight size={15} /></Button></>}
        />

        <section className="student-metrics" aria-label="Your volunteering summary">
          <Metric label="Active projects" value={projects.length} detail="Projects you have joined" icon={Folder} />
          <Metric label="Approved hours" value={numberFormat.format(dashboard.approvedHours)} detail="Verified contribution" icon={Clock} />
          <Metric label="Pending hours" value={numberFormat.format(dashboard.pendingHours)} detail="Waiting for review" icon={Hourglass} />
          <Metric label="Leaderboard rank" value={dashboard.currentUser ? `#${dashboard.currentUser.rank}` : "—"} detail={dashboard.currentUser ? `${numberFormat.format(dashboard.currentUser.hours || 0)} approved hrs` : "No rank available yet"} icon={Trophy} />
        </section>

        <section className="student-dashboard-panel student-calendar-section" style={{ marginTop: '24px', marginBottom: '24px' }}>
          <SectionHeader title="My schedule" description="Upcoming volunteer shifts you have registered for." />
          <StudentCalendar />
        </section>

        <section className="student-dashboard-grid student-dashboard-grid--overview">
          <article className="student-dashboard-panel student-dashboard-panel--chart">
            <SectionHeader title="Hours by project" description="Approved and pending activity across your projects." />
            {dashboard.projectHours.length ? (
              <div className="student-hours-chart" style={{ height: `${Math.min(420, Math.max(240, dashboard.projectHours.length * 42 + 80))}px` }}>
                <Bar data={chartData} options={chartOptions} />
              </div>
            ) : <div className="student-panel-state">Join a project to start tracking your contribution.</div>}
          </article>

          <article className="student-dashboard-panel">
            <SectionHeader title="Activity status" description="How your submitted hours are progressing." />
            <div className="student-approval-summary">
              <strong className="wcco-technical">{numberFormat.format(approvalShare)}%</strong>
              <span>of submitted hours approved</span>
            </div>
            <div className="student-progress" aria-label={`${numberFormat.format(approvalShare)} percent approved`}><span style={{ width: `${approvalShare}%` }} /></div>
            <dl className="student-hour-breakdown">
              <div><dt>Approved</dt><dd className="wcco-technical">{numberFormat.format(dashboard.approvedHours)} hrs</dd></div>
              <div><dt>Pending</dt><dd className="wcco-technical">{numberFormat.format(dashboard.pendingHours)} hrs</dd></div>
              <div><dt>All submitted</dt><dd className="wcco-technical">{numberFormat.format(hourTotal)} hrs</dd></div>
            </dl>
            <div className="student-focus-areas">
              <h3>Your focus areas</h3>
              {dashboard.categoryRows.length ? dashboard.categoryRows.slice(0, 5).map((category) => (
                <div key={category.name}><span>{category.name}</span><strong>{category.count} project{category.count === 1 ? "" : "s"}</strong></div>
              )) : <p>No project categories yet.</p>}
            </div>
          </article>
        </section>

        <section className="student-dashboard-grid student-dashboard-grid--leaderboards">
          <article className="student-dashboard-panel">
            <SectionHeader title="Volunteer leaderboard" description="Approved volunteer hours across the WCCO community." action={<Trophy size={20} aria-hidden="true" />} />
            <RankingBoard data={leaderboard.topVolunteers} loading={leaderboardLoading} type="volunteers" currentUser={dashboard.currentUser} expanded={expandedBoards.volunteers} onToggle={() => setExpandedBoards((state) => ({ ...state, volunteers: !state.volunteers }))} />
          </article>
          <article className="student-dashboard-panel">
            <SectionHeader title="Project competition" description="Projects ranked by their volunteers’ approved hours." action={<Flame size={20} aria-hidden="true" />} />
            <RankingBoard data={leaderboard.topProjects} loading={leaderboardLoading} type="projects" expanded={expandedBoards.projects} onToggle={() => setExpandedBoards((state) => ({ ...state, projects: !state.projects }))} />
          </article>
        </section>

        <section className="student-dashboard-panel student-projects-panel">
          <SectionHeader title="My active projects" description="Your current programmes and recorded contribution." action={<Button variant="outline-primary" onClick={() => navigate("/myprojects")}>View projects</Button>} />
          {dashboard.projectHours.length ? (
            <div className="student-project-list">
              <div className="student-project-list__labels" aria-hidden="true"><span>Project</span><span>Focus</span><span>Hours</span><span>Status</span></div>
              {dashboard.projectHours.map((project) => (
                <div className="student-project-row" key={project._id}>
                  <strong>{project.name || "Unnamed project"}</strong>
                  <span>{project.categories?.join(", ") || "Uncategorised"}</span>
                  <span className="wcco-technical">{numberFormat.format(project.allHours)} hrs</span>
                  <span className="wcco-status">{project.approved ? "Active" : "Pending approval"}</span>
                </div>
              ))}
            </div>
          ) : <div className="student-panel-state"><span>You have not joined any projects yet.</span><Button onClick={() => navigate("/browse")}>Browse projects</Button></div>}
        </section>
      </main>
    </AppShell>
  );
};

export default StudentDashboard;
